import { Component, Inject, Input, Optional } from '@angular/core';
import { FileService } from '@app/core/services/file.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { DocArcService, DocumentCategoryItem } from '@app/services/doc-arc.service';
import { DocArcComponent, type DocArcPage } from '../doc-arc.component';

@Component({
    selector: 'tt-doc-arc-information',
    templateUrl: './doc-arc-information.component.html',
    styleUrls: ['./doc-arc-information.component.css'],
    standalone: false,
})
export class DocArcInformationComponent {
    @Input()
    public page?: DocArcPage;

    @Input()
    public documentCategories: DocumentCategoryItem[] = [];

    public saving = false;

    public deleting = false;

    constructor(
        private docarc: DocArcService,
        private file: FileService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, DocArcComponent);
    }

    public get canShowDocument(): boolean {
        if (!this.page?.imageLink) return false;
        if (this.file.isOfficeContent(this.page.contentType ?? '')) return true;

        const localFileName = this.page.localFileName ?? this.page.docfilename;

        if (localFileName.toLowerCase().endsWith('.pdf')) return true;

        const fileExtension = localFileName.split('.').pop()?.toLowerCase() ?? '';
        const thumbnailFileName = (this.page.thumbnailLink ?? '').split('/').pop() ?? '';
        const thumbnailNameWithoutExtension = thumbnailFileName.split('.').slice(0, -1).join('.').toLowerCase();

        return !!thumbnailFileName && thumbnailNameWithoutExtension !== fileExtension;
    }

    public showDocument(): void {
        if (!this.page?.imageLink) return;

        const tokenLink = this.file.getTokenLink(this.page.imageLink);
        const isImage = this.isImage(this.page);
        const tab = window.open(isImage ? '' : tokenLink, '_blank');
        if (!tab) return;

        tab.opener = null;

        if (isImage) {
            const image = tab.document.createElement('img');
            image.src = tokenLink;
            image.alt = this.page.docfilename;
            tab.document.body.appendChild(image);
        }
    }

    private isImage(page: DocArcPage): boolean {
        return page.contentType?.startsWith('image/') === true || /\.(?:avif|bmp|gif|heic|heif|jpe?g|png|svg|tiff?|webp)$/i.test(page.docfilename);
    }

    public downloadDocument(): void {
        if (!this.page?.imageLink) return;

        const target = window.navigator.userAgent.includes('gonative') ? '_blank' : '_self';
        window.open(this.file.getTokenLink(this.page.imageLink), target);
    }

    public async save(): Promise<void> {
        if (!this.page) return;

        try {
            this.saving = true;

            this.page.wopiIsConverted = false;

            await this.docarc.saveDocArc(this.page);

            this.navigator?.back();
        } finally {
            this.saving = false;
        }
    }

    public async deleteDocArc(): Promise<void> {
        if (!this.page) return;

        try {
            this.deleting = true;

            await this.docarc.deleteDocArc({ docarc_keyno: this.page.docarc_keyno });

            this.navigator?.back();
        } finally {
            this.deleting = false;
        }
    }
}
